import { motion } from "framer-motion";
import { Layout, Search, Rocket, Palette, PenTool, LineChart, Sparkles, TrendingUp, ArrowUpRight } from "lucide-react";
import { MOCKS } from "@/components/mocks/ServiceMocks";

const services = [
  {
    icon: Layout, mock: "website",
    title: "Website Design & Development",
    desc: "Custom, conversion-driven websites engineered on modern stacks — fast, accessible, and on-brand from hero to footer.",
    tint: "glass-tint-blue", dot: "text-apex-blue",
    tags: ["Next.js", "Webflow", "Headless CMS"],
  },
  {
    icon: Search, mock: "seo",
    title: "SEO Optimization",
    desc: "Technical SEO, topical clusters, on-page structure, and internal linking that compound organic traffic month after month.",
    tint: "glass-tint-cyan", dot: "text-apex-cyan",
    tags: ["Technical", "On-page", "Content"],
  },
  {
    icon: Sparkles, mock: "aeo",
    title: "AEO & GEO for AI Search",
    desc: "Get cited by ChatGPT, Perplexity, Google AI Overviews, and Gemini. Structured data + entity SEO + answer-first content.",
    tint: "glass-tint-purple", dot: "text-apex-purpleSoft",
    tags: ["ChatGPT", "Perplexity", "AI Overviews"],
    highlight: true,
  },
  {
    icon: Rocket, mock: "landing",
    title: "Landing Page Design",
    desc: "Focused pages that turn cold traffic into qualified leads — sharp copy, tight UX, and A/B-ready flows.",
    tint: "glass-tint-orange", dot: "text-apex-orange",
    tags: ["CRO", "A/B testing", "Copy"],
  },
  {
    icon: Palette, mock: "branding",
    title: "Branding & Visual Identity",
    desc: "Logos, palettes, type systems, and voice guidelines that make your business unmistakable across every touchpoint.",
    tint: "glass-tint-pink", dot: "text-apex-pink",
    tags: ["Logo", "Guidelines", "Design system"],
  },
  {
    icon: PenTool, mock: "content",
    title: "Content Strategy",
    desc: "Editorial roadmaps and search-intent-mapped content that build topical authority and rank for what buyers actually search.",
    tint: "glass-tint-mint", dot: "text-apex-mint",
    tags: ["Editorial", "Intent", "Clusters"],
  },
  {
    icon: LineChart, mock: "performance",
    title: "Performance Tracking",
    desc: "GA4, Search Console, dashboards, and monthly reporting so every dollar and click tracks back to real business outcomes.",
    tint: "glass-tint-blue", dot: "text-apex-blueSoft",
    tags: ["GA4", "Looker", "Attribution"],
  },
  {
    icon: TrendingUp, mock: "cro",
    title: "Conversion Optimization",
    desc: "Funnel audits, heatmaps, session replay, and experimentation — squeeze more revenue from the traffic you already have.",
    tint: "glass-tint-cyan", dot: "text-apex-cyan",
    tags: ["Heatmaps", "CRO", "Analytics"],
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="section-pad relative">
      <div className="container-apex">
        <div className="max-w-3xl">
          <div className="eyebrow mb-4">
            <span className="w-6 h-px bg-apex-cyan" /> What we do
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
            Everything you need to turn your website into a{" "}
            <span className="text-apex-orange">growth engine.</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed max-w-2xl">
            One team for design, SEO, AEO, content, and performance — so strategy, story, and structure move in lockstep across Google <em>and</em> AI search.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            const Mock = MOCKS[s.mock];
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className={`${s.tint} glass-hover glass-inner-glow p-0 group relative overflow-hidden ${s.highlight ? "aurora-border" : ""}`}
                data-testid={`service-card-${i}`}
              >
                {/* Live-style CSS/SVG mockup — no external image */}
                <div className="relative h-40 p-4 overflow-hidden">
                  <div className="absolute inset-0 opacity-70 pointer-events-none" style={{
                    background: "radial-gradient(120% 80% at 50% 0%, rgba(255,255,255,0.05), transparent 60%)"
                  }} />
                  <Mock />
                  <div className="absolute top-3 right-3">
                    <ArrowUpRight className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
                  </div>
                  {s.highlight && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-bold uppercase tracking-widest text-white z-10">
                      <Sparkles className="w-3 h-3 text-apex-cyan" />
                      New
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="p-6 border-t border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
                      <Icon className={`w-5 h-5 ${s.dot}`} strokeWidth={1.7} />
                    </div>
                    <h3 className="font-heading font-bold text-[17px] leading-tight">{s.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">{s.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {s.tags.map((t) => (
                      <span key={t} className="text-[10px] font-medium uppercase tracking-widest text-white/60 px-2 py-1 rounded-full bg-white/[0.04] border border-white/10">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
